import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { Accessibility, CimaCore, CimaToken, Util, XmStore, XmStoreUtil } from 'core/services';
import { CmsCore } from 'store/cms/models';
import { Account, StreetAddress, User } from 'store/user/models';
import { CONFIG_TOKEN, CORE_ACCOUNT_URL, MAKE_A_PAYMENT, MY_ACCOUNT, NewCookieConsentIds, StorageToken } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
import { SessionStorage } from 'core/services/storage/session';
import { LocationCollection } from 'store/locations/models/location';
import { StoreAction } from 'store/actions';

@Component({
    selector: 'xm-primary-nav',
    styleUrls: ['./primary.scss'],
    templateUrl: './primary.html',
    standalone: false
})

export class NavPrimaryComponent implements OnDestroy, OnInit {
    @ViewChild('xmNavHamburgerRoot', { read: ElementRef, static: true }) public root: ElementRef;
    @Input() public hideSkipToContent: boolean;

    public isMobileNavOpen: boolean = false;
    public isNavOpen: boolean = false;
    public checkHamburgerNavExpanded: boolean = false;
    public abTestExperience: string;
    public desktopMenuItems: CmsNavMenuItems[];
    public account: Account;
    public serviceAddress: StreetAddress;
    public isOrionTenant: boolean = false;
    public pageData: SiteNav;
    public myAccountMenuItems: CmsNavMenuItems;
    public showMyAccountChildren: boolean = true;
    public displayName: string;
    public linkedAccounts: LocationRow[] = [];
    public isCBMAccountSelectorEnabled: boolean = false;
    public isOrionAccountSelectorEnabled: boolean = false;
    public locationData: LocationCollection;
    public isLinkedAccountSelector: boolean = false;
    private subscriptions: Subscription[] = [];
    private myAccountNavOptionsChildren: CmsNavMenuItems[];
    private myAccountAuthenticatedNavOptionsChildren: CmsNavMenuItems[];
   
    private accessibility: Accessibility;
    private cimaToken: CimaToken;
    private xmStore: XmStore;
    private cimaCore: CimaCore;
    private config: IXMOptions;
    private bodyElement: HTMLElement;
    private renderer: Renderer2;
    private sessionStorage: SessionStorage;

    constructor(renderer: Renderer2, accessibility: Accessibility, cimaToken: CimaToken, xmStore: XmStore, cimaCore: CimaCore, sessionStorage: SessionStorage, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { renderer, accessibility, cimaToken, xmStore, cimaCore, sessionStorage, config });
    }

    public ngOnInit(): void {
        this.isOrionTenant = this.sessionStorage.get(StorageToken.X_TENANT_ID) === 'ORION';
        this.isCBMAccountSelectorEnabled = this.config.ENABLE_CBM_ACCOUNT_SELECTOR && !this.isOrionTenant;
        this.isOrionAccountSelectorEnabled = this.config.ENABLE_ORION_ACCOUNT_SELECTOR && this.isOrionTenant;
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
            this.pageData = result.nav;
            this.desktopMenuItems = this.cimaCore.isBusinessUser ? this.pageData.desktopCbmLeftMenuItems : this.pageData.desktopLeftMenuItems;
            this.myAccountMenuItems = { ...result.nav.myAccount };
            this.myAccountAuthenticatedNavOptionsChildren = [ ...this.myAccountMenuItems.children ];
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User, { returnUndefined: true }), (user: User) => {
            if (this.cimaCore.isBusinessUser) {
                this.displayName = user?.displayName || '';
            } else {
                this.displayName = user?.loggedInUserName || '';
            }
            this.account = user?.account;
            this.serviceAddress = user?.account?.serviceAddress;

            if (this.cimaToken.isLoggedIn) {
                this.myAccountNavOptionsChildren = [ ...this.myAccountAuthenticatedNavOptionsChildren ];
                this.myAccountMenuItems.name = this.displayName || MY_ACCOUNT;
                this.myAccountMenuItems.isFullName = true;

                const children: CmsNavMenuItems[] = this.displayName ? [ ...this.myAccountNavOptionsChildren ] : this.myAccountNavOptionsChildren.filter((child: CmsNavMenuItems) => child.name !== MY_ACCOUNT);
                this.myAccountMenuItems = { ... this.myAccountMenuItems, children: this.filterBusinessOnlyItems(children) };
            } else {
                this.myAccountNavOptionsChildren = [ ...this.myAccountMenuItems.unaunthenticatedChildren ];
                const children: CmsNavMenuItems[] = [ ...this.myAccountNavOptionsChildren ];
                this.myAccountMenuItems = { ... this.myAccountMenuItems, children: this.filterBusinessOnlyItems(children) };
            }

            this.isLinkedAccountSelector = this.isBusinessUser && this.account?.authGuid && (this.isCBMAccountSelectorEnabled || this.isOrionAccountSelectorEnabled);
            if (this.isLinkedAccountSelector) {
                this.getLinkedAccount();
            }
        }));

        this.initialCookieConsent();
    }

    public getLinkedAccount(): void {
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.find<LocationCollection>(StoreAction.GET_SWITCH_LOCATIONS, LocationCollection), (data: LocationCollection) => {
            this.locationData = data;
            const accountList: LocationRow[] = this.locationData?.locationRows ?? [];
            this.linkedAccounts = accountList?.map((account: LocationRow) => {
                const [address = '', cityStateZip]: string[] = account.location?.split('\n') ?? [];

                return { ...account, address: address.trim(), cityStateZip: cityStateZip?.trim() ?? '' };
            });
        }));
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public focusContent(): void {
        this.accessibility.focusHeader();
    }

    public closeHamburgerNavMenu(): void {
        this.checkHamburgerNavExpanded = !this.checkHamburgerNavExpanded;
    }

    public focusoutHamburgerMenu(event: FocusEvent): void {
        // Support for IE
        const relatedTargetElement: EventTarget = document.activeElement === document.body ? event.relatedTarget : document.activeElement;

        if (this.isNavOpen && (!relatedTargetElement || !this.root.nativeElement.contains(relatedTargetElement))) {
            this.accessibility.announceReaderText(this.pageData.navCloseReaderText);
        }
    }

    public isMobileNavExpand(isNavOpen: boolean): void {
        this.isNavOpen = isNavOpen;
    }

    public get isBusinessUser(): boolean {
        return this.cimaCore.isBusinessUser;
    }

    private filterBusinessOnlyItems(children: CmsNavMenuItems[]): CmsNavMenuItems[] {
        return this.cimaCore.isBusinessUser ? children.filter((child: CmsNavMenuItems) => child.name !== MAKE_A_PAYMENT && !child.linkDecoration?.includes(CORE_ACCOUNT_URL)) : children;
    }

    private initialCookieConsent(): void {
        if (!document.getElementById('ot-sdk-btn')&& !(window.location.href.includes('standalone-cpc'))) {
            this.bodyElement = window.document.body;
            const consent: Element = this.bodyElement.querySelector('.cookie-consent');
            consent.insertAdjacentHTML('beforeend', '<div class="ot-sdk-btn"><button id="ot-sdk-btn" class="ot-sdk-show-settings">Cookie Settings</button></div>');
            this.buildAutoBlockingScript();
            this.buildCookieConsentScript();
        }
    }

    private buildCookieConsentScript(): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cookieScript: any = this.renderer.createElement('script');
        cookieScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
        this.renderer.setAttribute(cookieScript, 'data-document-language', 'true');

        if (['prod'].includes(this.config.LOG_ENV)) {
            this.renderer.setAttribute(cookieScript, 'data-domain-script', NewCookieConsentIds.DATA_DOMAIN_ID_PROD); 
        } else {
            this.renderer.setAttribute(cookieScript, 'data-domain-script', NewCookieConsentIds.DATA_DOMAIN_ID_LOWERS); 
        }

        this.renderer.setAttribute(cookieScript, 'type', 'text/javascript');
        this.renderer.setAttribute(cookieScript, 'charset', 'UTF-8');
        cookieScript.defer = true;
        this.renderer.appendChild(document.head, cookieScript);
    }

    private buildAutoBlockingScript(): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const autoBlockScript: any = this.renderer.createElement('script');
        if (['prod'].includes(this.config.LOG_ENV)) {
            autoBlockScript.src = `https://cdn.cookielaw.org/consent/${NewCookieConsentIds.DATA_DOMAIN_ID_PROD}/OtAutoBlock.js`;
        } else {
            autoBlockScript.src = `https://cdn.cookielaw.org/consent/${NewCookieConsentIds.DATA_DOMAIN_ID_LOWERS}/OtAutoBlock.js`;
        }
        this.renderer.setAttribute(autoBlockScript, 'type', 'text/javascript');
        this.renderer.insertBefore(document.head, autoBlockScript, document.head.firstChild);
    }
}
